import { useState } from 'react';

import { ChartFigure } from './ChartFigure';
import { pieSlices } from './geometry';

export interface PieDatum {
  label: string;
  value: number;
  /** Slice colour token. */
  color: string;
  /**
   * The value as the reader should see it — `"1,392"`, `"41k"`, `"£12.40"`.
   * Formatting belongs to the caller, which knows whether a value is documents,
   * pages or currency. Falls back to the raw number.
   */
  valueLabel?: string;
}

export interface PieProps {
  data: readonly PieDatum[];
  summary: string;
  /**
   * Rendered edge length in px (the disc is square). The `viewBox` is fixed, so
   * this scales the drawing rather than re-laying it out, and CSS caps it at the
   * container width — so this is a maximum, not a promise. Defaults to 160; pass
   * more where the pie is the subject of the view rather than a thumbnail.
   */
  size?: number;
}

/**
 * The seam between wedges, in `viewBox` units, chosen by how many wedges there
 * are.
 *
 * A seam keeps neighbouring wedges distinct when the palette repeats — with more
 * types than palette tokens, two same-coloured wedges *will* end up adjacent. But
 * the outline is drawn on both sides of every boundary, so on a crowded pie it
 * stops separating wedges and starts eating them: the smallest type in a real
 * 41-type mix was 23 documents of 11,000, which is 0.75° — an arc of about 1.26
 * units, less than the 1.5-unit outline that was erasing it. The result looked
 * like gaps punched out of the disc.
 */
function seamWidth(sliceCount: number): number {
  return sliceCount > 16 ? 0.4 : 1.5;
}

/**
 * Leader-line geometry, in `viewBox` units (centre 100,100, arc radius 96).
 *
 * Kept deliberately short. Every unit the line reaches past the arc is a unit the
 * label card is pushed further out of the container, and at a 480px disc one
 * viewBox unit is 2.4 real pixels — so a generous-looking elbow puts the card
 * outside the dialog entirely.
 */
const LEADER = {
  /** Just inside the arc, so the line reads as touching the wedge. */
  start: 92,
  /** Where the line turns from radial to horizontal. */
  elbow: 104,
  /** The horizontal tail the card hangs off. */
  run: 10,
} as const;

/** `pieSlices` places angle 0 at twelve o'clock and sweeps clockwise. */
function polar(radius: number, theta: number): { x: number; y: number } {
  return { x: 100 + radius * Math.sin(theta), y: 100 - radius * Math.cos(theta) };
}

/**
 * A true pie (no centre hole) for the Document type mix breakdown — drawn as
 * true wedge paths (`pieSlices()`), not `donutSlices()`'s stroke-dasharray ring
 * trick: with a dozen-plus thin slices, dashed-stroke seams anti-alias into a
 * visibly speckled/"grainy" ring, where explicit wedges sharing an exact
 * boundary vertex render as one clean, seam-free disc.
 *
 * **Labels appear on hover only.** A wedge carries no label of its own, and a
 * legend cannot be matched to one by eye once the palette repeats — but drawing
 * all 40 labels at once is unreadable, and the sub-degree wedges have nowhere to
 * put one. So the hovered wedge alone grows a leader line out to its name and
 * value, which is legible at any slice count.
 */
export function Pie({ data, summary, size = 160 }: PieProps): React.JSX.Element {
  const slices = pieSlices(data.map((d) => d.value));
  const seam = seamWidth(data.length);
  const [hovered, setHovered] = useState<number | null>(null);

  // Mid-angle per wedge, from the running total of the fractions `pieSlices`
  // already computed — so the line can never disagree with the wedge it points
  // at, which recomputing the angles here separately would risk.
  let acc = 0;
  const midAngles = slices.map((slice) => {
    const mid = acc + slice.frac / 2;
    acc += slice.frac;
    return mid * Math.PI * 2;
  });

  const active = hovered !== null ? data[hovered] : undefined;
  const theta = hovered !== null ? (midAngles[hovered] ?? 0) : 0;
  const arc = polar(LEADER.start, theta);
  const elbow = polar(LEADER.elbow, theta);
  // Which way the tail runs: outward from the disc, so the label never lies
  // across the pie it describes.
  const rightward = Math.sin(theta) >= 0;
  const tail = { x: elbow.x + (rightward ? LEADER.run : -LEADER.run), y: elbow.y };

  return (
    <ChartFigure
      summary={summary}
      columns={['Type', 'Share']}
      tableCaption={summary}
      rows={data.map((d, i) => ({ label: d.label, values: { Share: `${String(slices[i]?.pct ?? 0)}%` } }))}
    >
      <div
        className="ds-pie"
        onMouseLeave={() => {
          setHovered(null);
        }}
      >
        <svg
          className="ds-pie__disc"
          width={size}
          height={size}
          viewBox="0 0 200 200"
          role="img"
          aria-label={summary}
        >
          {data.map((datum, i) => {
            const slice = slices[i];
            if (slice === undefined || slice.d === '') return null;
            return (
              <path
                key={datum.label}
                className="ds-pie__slice"
                d={slice.d}
                fill={datum.color}
                stroke="var(--card)"
                strokeWidth={seam}
                strokeLinejoin="round"
                onMouseEnter={() => {
                  setHovered(i);
                }}
              />
            );
          })}
          {active !== undefined ? (
            // Drawn after the wedges so it is never overlapped by one, and in the
            // wedge's own colour so the line and the slice are obviously a pair.
            // Escapes the viewBox — see `overflow: visible` on `.ds-pie__disc`.
            <polyline
              className="ds-pie__leader"
              points={`${String(arc.x)},${String(arc.y)} ${String(elbow.x)},${String(elbow.y)} ${String(tail.x)},${String(tail.y)}`}
              fill="none"
              stroke={active.color}
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          ) : null}
        </svg>
        {active !== undefined ? (
          // HTML rather than SVG <text>: text inside the viewBox scales with the
          // disc, so at 480px a label would render at ~20px and a long type name
          // would run several hundred pixels wide. Positioned as a percentage of
          // the box, which needs no measuring and tracks the disc as it resizes.
          <div
            className={rightward ? 'ds-pie__label' : 'ds-pie__label ds-pie__label--left'}
            aria-hidden="true"
            style={{ left: `${String((tail.x / 200) * 100)}%`, top: `${String((tail.y / 200) * 100)}%` }}
          >
            <span className="ds-pie__label-name">{active.label}</span>
            <span className="ds-pie__label-val">{active.valueLabel ?? String(active.value)}</span>
          </div>
        ) : null}
      </div>
    </ChartFigure>
  );
}
